// =============================================================================
// SalesOrdersAdmin.jsx  --  Unleashed → Sales Orders (admin viewer)
// =============================================================================
// Read-only viewer for the Unleashed Sales Orders staged in `request_sales_orders`
// (db-62). Two views: a searchable LIST of orders (grouped by order number), and a
// DETAIL that mirrors the Unleashed "View Sales Order" layout — header fields up
// top, the Order Lines table below, plus the request link + sent/received-Unleashed
// timestamps. Populated later by the Order Builder; shows an empty state until then.
// =============================================================================

import React from "react";
import {
  listPushes,
  requeuePush,
  setApiExcludedForOrders,
  setExportedForOrders,
} from "../../data/salesOrderPushes";
import { listSalesOrders } from "../../data/salesOrders";
import { usePageSize } from "../../hooks/usePageSize";
import { confirmDialog } from "../../services/dialogService";
import {
  distinctSuffixes,
  distinctWarehouses,
  filterSalesOrders,
  groupOrdersByRequest,
  NO_PUSH,
  rollupStatus,
} from "../../services/salesOrderFilter";
import { formatUnleashedPushError } from "../../services/unleashedPushError";
import { csvFilename, toUnleashedCsv } from "../../services/unleashedSalesOrderCsv";
import { ModalBackdrop } from "../form/FormUI";
import { SalesOrderInspectorModal } from "./SalesOrderInspectorModal.jsx";

// Trigger a client-side CSV download (no server round-trip, no credentials).
function downloadCsv(text, filename) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Push-status badge colours (request_sales_order_pushes.status).
const STATUS_STYLE = {
  queued: { bg: "#ECEFF1", fg: "#455A64" },
  pushing: { bg: "#E3F2FD", fg: "#1565C0" },
  pushed: { bg: "#E8F5E9", fg: "#2E7D32" },
  failed: { bg: "#FFEBEE", fg: "#C62828" },
  retry_pending: { bg: "#FFF7E6", fg: "#8a5a00" },
  // db-113: parked out of the worker's reach until a picker releases it. Had no entry,
  // so it fell through to the same grey as 'queued' and was indistinguishable.
  hold: { bg: "#F3E5F5", fg: "#6A1B9A" },
};

function StatusBadge({ status }) {
  if (!status) return <span style={{ fontSize: 12, color: "#607D79" }}>—</span>;
  const s = STATUS_STYLE[status] || { bg: "#ECEFF1", fg: "#455A64" };
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        color: s.fg,
        background: s.bg,
        borderRadius: 4,
        padding: "2px 7px",
        whiteSpace: "nowrap",
      }}
    >
      {status.replace("_", " ")}
    </span>
  );
}

// --- small display helpers ---------------------------------------------------
function dash(v) {
  return v === null || v === undefined || v === "" ? "—" : v;
}
function money(v) {
  const n = Number.parseFloat(v);
  return Number.isFinite(n)
    ? `$${n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";
}

// A label / value pair (Unleashed-style: right-aligned label, boxed value).
function Field({ label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
      <div
        style={{ width: 130, textAlign: "right", fontSize: 12, color: "#607D79", flexShrink: 0 }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, color: "#1A2E2B", fontWeight: 500, wordBreak: "break-word" }}>
        {dash(value)}
      </div>
    </div>
  );
}

const th = {
  textAlign: "left",
  padding: "8px 12px",
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: "#607D79",
  borderBottom: "2px solid #E0E0E0",
  whiteSpace: "nowrap",
};
const td = { padding: "9px 12px", fontSize: 13, borderBottom: "1px solid #F0F0F0" };

const inputStyle = {
  padding: "6px 10px",
  borderRadius: 6,
  border: "1px solid #ccc",
  fontSize: 13,
  minWidth: 220,
};
const selectStyle = {
  padding: "6px 8px",
  borderRadius: 6,
  border: "1px solid #ccc",
  fontSize: 13,
};
const bulkBtn = {
  background: "white",
  border: "1px solid #00897B",
  color: "#00695C",
  borderRadius: 6,
  padding: "5px 10px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};
const chip = {
  display: "inline-block",
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.3px",
  borderRadius: 4,
  padding: "1px 5px",
  marginLeft: 4,
};

// ── Detail view ──────────────────────────────────────────────────────────────
function SalesOrderDetail({ order, onBack, push, onRequeue }) {
  const h = order.header;
  const [retrying, setRetrying] = React.useState(false);
  const parsedError = push?.error_message ? formatUnleashedPushError(push.error_message) : null;
  // 'pushing' is included so a run that died mid-POST can be recovered — but the
  // handler confirms first, because re-queuing one that DID reach Unleashed
  // duplicates the order. Finding B16 item 11.
  const stuckPushing = push?.status === "pushing";
  const canRetry =
    !!push &&
    (push.status === "retry_pending" ||
      push.status === "failed" ||
      stuckPushing ||
      !!push.error_message);

  async function retry() {
    if (!onRequeue) return;
    setRetrying(true);
    await onRequeue(order.orderNumber, push?.status);
    setRetrying(false);
  }

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        style={{
          background: "none",
          border: "none",
          color: "#00897B",
          fontWeight: 600,
          cursor: "pointer",
          padding: 0,
          fontSize: 14,
          marginBottom: 14,
        }}
      >
        ← Back to Sales Orders
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <h2 style={{ margin: 0, color: "#00695C", fontSize: 22 }}>{dash(order.orderNumber)}</h2>
        {h.request_id && (
          <span style={{ fontSize: 12, color: "#607D79" }}>
            Request{" "}
            <button
              type="button"
              onClick={function () {
                window.open(`/requests/${h.request_id}`, "_blank");
              }}
              style={{
                background: "none",
                border: "none",
                color: "#00897B",
                textDecoration: "underline",
                cursor: "pointer",
                padding: 0,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              #{h.request_id}
            </button>
          </span>
        )}
        <button
          type="button"
          onClick={function () {
            downloadCsv(toUnleashedCsv([order]), csvFilename());
          }}
          style={{
            marginLeft: "auto",
            background: "#00897B",
            color: "white",
            border: "none",
            borderRadius: 6,
            padding: "6px 12px",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Export CSV
        </button>
      </div>

      {/* Header fields — three columns, mirroring the Unleashed order screen. */}
      <div
        style={{
          background: "white",
          borderRadius: 10,
          border: "1px solid #CFE0DE",
          padding: "16px 18px",
          marginBottom: 16,
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 24,
        }}
      >
        <div>
          <Field label="Customer Code" value={h.customer_code} />
          <Field label="Customer Ref" value={h.customer_reference} />
          <Field label="Order Warehouse" value={h.warehouse_code} />
          <Field label="Tax Code" value={h.tax_code} />
          <Field label="Discount (%)" value={h.discount_percent} />
          <Field label="Delivery Instr." value={h.delivery_instruction} />
        </div>
        <div>
          <Field label="Delivery Contact" value={h.delivery_contact} />
          <Field label="Delivery Name" value={h.delivery_name} />
          <Field label="Address 1" value={h.delivery_street_address} />
          <Field label="Address 2" value={h.delivery_street_address_2} />
          <Field label="Suburb/City" value={h.delivery_city} />
          <Field label="State/Region" value={h.delivery_region} />
          <Field label="Post Code" value={h.delivery_post_code} />
          <Field label="Country" value={h.delivery_country} />
        </div>
        <div>
          <Field label="Order Date" value={h.order_date} />
          <Field label="Required Date" value={h.required_date} />
          <Field label="Quote Expiry" value={h.quote_expiry_date} />
          <Field label="Sales Group" value={h.sales_order_group} />
          <Field label="Sales Person" value={h.sales_person_email} />
        </div>
        {h.comments && (
          <div style={{ gridColumn: "1 / -1", borderTop: "1px solid #EEF3F2", paddingTop: 12 }}>
            <div style={{ fontSize: 12, color: "#607D79", marginBottom: 4 }}>
              Sales Order Comment
            </div>
            <div style={{ fontSize: 13, color: "#1A2E2B", whiteSpace: "pre-wrap" }}>
              {h.comments}
            </div>
          </div>
        )}
      </div>

      {/* Unleashed push status */}
      <div
        style={{
          background: "white",
          borderRadius: 10,
          border: "1px solid #CFE0DE",
          padding: "12px 18px",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: "#607D79" }}>Unleashed push</span>
          <StatusBadge status={push ? push.status : null} />
        </div>
        {push?.suffix && <Field label="SO Type" value={push.suffix} />}
        <Field label="Attempts" value={push ? push.attempt_count : "—"} />
        <Field label="Queued" value={push ? push.created_at : "—"} />
        <Field label="Pushed" value={push ? push.pushed_at : null} />
        {push?.unleashed_guid && <Field label="Unleashed GUID" value={push.unleashed_guid} />}
        {canRetry && (
          <button
            type="button"
            onClick={retry}
            disabled={retrying}
            title={
              stuckPushing
                ? "This order is stuck mid-send. Re-queuing asks for confirmation first — if the original send reached Unleashed it would create a duplicate."
                : "Reset this order to queued so the push worker retries it"
            }
            style={{
              marginLeft: "auto",
              background: stuckPushing ? "#B71C1C" : "#00695C",
              color: "white",
              border: "none",
              borderRadius: 6,
              padding: "7px 16px",
              fontSize: 13,
              fontWeight: 700,
              cursor: retrying ? "default" : "pointer",
              fontFamily: "inherit",
              opacity: retrying ? 0.6 : 1,
            }}
          >
            {retrying ? "Re-queuing…" : stuckPushing ? "Re-queue stuck order" : "Retry now"}
          </button>
        )}
        {parsedError && (
          <div
            style={{
              flexBasis: "100%",
              background: "#FFF5F5",
              border: "1px solid #F0C6C6",
              borderRadius: 8,
              padding: "10px 12px",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "#C62828",
                textTransform: "uppercase",
                letterSpacing: 0.4,
                marginBottom: 4,
              }}
            >
              Last error{parsedError.code ? ` — HTTP ${parsedError.code}` : ""}
            </div>
            <div style={{ fontSize: 13, color: "#1A2E2B", whiteSpace: "pre-wrap" }}>
              {parsedError.message || "(no message)"}
            </div>
            {/* Raw Unleashed response, collapsed — select-all so it's easy to copy. */}
            <details style={{ marginTop: 8 }}>
              <summary style={{ fontSize: 12, color: "#607D79", cursor: "pointer" }}>
                Show raw response (copy/paste)
              </summary>
              <pre
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  background: "#FBECEC",
                  border: "1px solid #F0C6C6",
                  borderRadius: 6,
                  padding: "8px 10px",
                  userSelect: "all",
                }}
              >
                {parsedError.raw}
              </pre>
            </details>
          </div>
        )}
      </div>

      {/* Order lines */}
      <div
        style={{
          background: "white",
          borderRadius: 10,
          border: "1px solid #CFE0DE",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: "#37474F",
            color: "white",
            padding: "10px 14px",
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          Order Lines ({order.lineCount})
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#FAFAFA" }}>
                <th style={th}>Ln</th>
                <th style={th}>Product Code</th>
                <th style={{ ...th, textAlign: "right" }}>Quantity</th>
                <th style={{ ...th, textAlign: "right" }}>Unit Price</th>
                <th style={{ ...th, textAlign: "right" }}>Line Disc. (%)</th>
                <th style={th}>SO Line Comment</th>
              </tr>
            </thead>
            <tbody>
              {order.lines.map(function (ln) {
                return (
                  <tr key={ln.id}>
                    <td style={td}>{dash(ln.line_number)}</td>
                    <td style={{ ...td, fontWeight: 600, color: "#00695C" }}>
                      {dash(ln.product_code)}
                    </td>
                    <td style={{ ...td, textAlign: "right" }}>{dash(ln.order_quantity)}</td>
                    <td style={{ ...td, textAlign: "right" }}>{money(ln.unit_price)}</td>
                    <td style={{ ...td, textAlign: "right" }}>{dash(ln.line_discount_percent)}</td>
                    <td style={td}>{dash(ln.line_comments)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── List + container ─────────────────────────────────────────────────────────
export function SalesOrdersAdmin() {
  const [orders, setOrders] = React.useState([]);
  const [pushes, setPushes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [inspectOpen, setInspectOpen] = React.useState(false);
  const [criteria, setCriteria] = React.useState({});
  const [selected, setSelected] = React.useState(null);
  const [picked, setPicked] = React.useState(function () {
    return new Set();
  });
  const [busy, setBusy] = React.useState(false);
  const [expanded, setExpanded] = React.useState(function () {
    return new Set();
  });
  const setCrit = React.useCallback(function (patch) {
    setCriteria(function (c) {
      return { ...c, ...patch };
    });
  }, []);

  const toggleExpand = React.useCallback(function (key) {
    setExpanded(function (prev) {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  React.useEffect(function () {
    Promise.all([listSalesOrders(), listPushes()]).then(function (res) {
      setOrders(res[0] || []);
      setPushes(res[1] || []);
      setLoading(false);
    });
  }, []);

  // Push lifecycle row keyed by order_number (one per order).
  const pushByOrder = React.useMemo(
    function () {
      const m = {};
      for (const p of pushes) m[p.order_number] = p;
      return m;
    },
    [pushes]
  );

  const suffixOptions = React.useMemo(
    function () {
      return distinctSuffixes(pushes);
    },
    [pushes]
  );
  const warehouseOptions = React.useMemo(
    function () {
      return distinctWarehouses(orders);
    },
    [orders]
  );
  const filtered = React.useMemo(
    function () {
      return filterSalesOrders(orders, pushByOrder, criteria);
    },
    [orders, pushByOrder, criteria]
  );

  // The filtered SOs grouped by request (parent row + expandable child SOs).
  const groups = React.useMemo(
    function () {
      return groupOrdersByRequest(filtered);
    },
    [filtered]
  );

  // Paginate the grouped list (20 request-groups per page). Reset to page 1
  // whenever the filter criteria change the result set.
  const [SO_PAGE_SIZE] = usePageSize();
  const [soPage, setSoPage] = React.useState(0);
  React.useEffect(
    function () {
      setSoPage(0);
    },
    [criteria]
  );
  const soTotalPages = Math.max(1, Math.ceil(groups.length / SO_PAGE_SIZE));
  const soSafePage = Math.min(soPage, soTotalPages - 1);
  const pagedGroups = groups.slice(soSafePage * SO_PAGE_SIZE, (soSafePage + 1) * SO_PAGE_SIZE);

  // Select / deselect every SO in a request group at once.
  const toggleGroup = React.useCallback(function (group) {
    setPicked(function (prev) {
      const next = new Set(prev);
      const allIn = group.orders.every(function (o) {
        return next.has(o.orderNumber);
      });
      for (const o of group.orders) {
        if (allIn) next.delete(o.orderNumber);
        else next.add(o.orderNumber);
      }
      return next;
    });
  }, []);

  const allFilteredPicked =
    filtered.length > 0 &&
    filtered.every(function (o) {
      return picked.has(o.orderNumber);
    });

  const toggleOne = React.useCallback(function (orderNumber) {
    setPicked(function (prev) {
      const next = new Set(prev);
      if (next.has(orderNumber)) next.delete(orderNumber);
      else next.add(orderNumber);
      return next;
    });
  }, []);

  const toggleAll = React.useCallback(
    function () {
      setPicked(function () {
        if (allFilteredPicked) return new Set();
        return new Set(
          filtered.map(function (o) {
            return o.orderNumber;
          })
        );
      });
    },
    [filtered, allFilteredPicked]
  );

  // Run a bulk write over the picked orders, then clear the selection and reload
  // the push rows so the marks refresh in place.
  const runBulk = React.useCallback(
    async function (fn) {
      const nums = [...picked];
      if (nums.length === 0) return;
      setBusy(true);
      await fn(nums);
      const res = await listPushes();
      setPushes(res || []);
      setPicked(new Set());
      setBusy(false);
    },
    [picked]
  );

  // Re-queue a single order from the detail's Retry button, then reload the push
  // rows so the badge/error refresh in place (the modal stays open on the order).
  //
  // A row stranded at 'pushing' (a worker run that died mid-POST) can only be
  // re-queued behind an explicit confirm: the worker mints a fresh GUID per attempt,
  // so if that POST actually reached Unleashed this creates a SECOND sales order —
  // and Unleashed reserves an order number forever, so the duplicate can't be undone
  // cleanly. The repository refuses 'pushing' unless allowStuckPushing is passed.
  // Finding B16 item 11 + John's escape-hatch call, 23 Aug 2026.
  const handleRequeue = React.useCallback(async function (orderNumber, status) {
    const stuck = status === "pushing";
    if (stuck) {
      const ok = await confirmDialog({
        title: "Re-queue a stuck order?",
        message:
          `${orderNumber} is still marked "pushing", which usually means a worker run stopped ` +
          "part-way through sending it.\n\nIf that send DID reach Unleashed, re-queuing will " +
          "create a SECOND sales order there, and Unleashed never releases an order number.\n\n" +
          "Check Unleashed for this order number before continuing.",
        confirmLabel: "Re-queue anyway",
        cancelLabel: "Cancel",
        danger: true,
      });
      if (!ok) return;
    }
    await requeuePush(orderNumber, { allowStuckPushing: stuck });
    const res = await listPushes();
    setPushes(res || []);
  }, []);

  // Export just the picked orders (no marking — "mark exported" is a separate action).
  const exportPicked = React.useCallback(
    function () {
      const chosen = orders.filter(function (o) {
        return picked.has(o.orderNumber);
      });
      if (chosen.length > 0) downloadCsv(toUnleashedCsv(chosen), csvFilename());
    },
    [orders, picked]
  );

  return (
    <div className="px-7 py-6 max-w-[var(--page-max)] mx-auto">
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 12, color: "#607D79", fontWeight: 600 }}>Unleashed</div>
        <h2 style={{ margin: 0, color: "#00695C", fontSize: 22 }}>Sales Orders</h2>
      </div>

      <div
        style={{
          background: "white",
          borderRadius: 10,
          boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            borderBottom: "1px solid #F0F0F0",
          }}
        >
          <input
            value={criteria.text || ""}
            onChange={function (e) {
              setCrit({ text: e.target.value });
            }}
            placeholder="Search order no., customer, reference…"
            style={inputStyle}
          />
          <select
            value={criteria.suffixes?.[0] || ""}
            onChange={function (e) {
              setCrit({ suffixes: e.target.value ? [e.target.value] : [] });
            }}
            style={selectStyle}
            aria-label="SO type"
          >
            <option value="">All types</option>
            {suffixOptions.map(function (s) {
              return (
                <option key={s} value={s}>
                  {s}
                </option>
              );
            })}
          </select>
          <select
            value={criteria.statuses?.[0] || ""}
            onChange={function (e) {
              setCrit({ statuses: e.target.value ? [e.target.value] : [] });
            }}
            style={selectStyle}
            aria-label="Push status"
          >
            <option value="">All statuses</option>
            <option value="queued">Queued</option>
            <option value="pushing">Pushing</option>
            <option value="pushed">Pushed</option>
            <option value="failed">Failed</option>
            <option value="retry_pending">Retry pending</option>
            <option value="hold">Held (awaiting picker)</option>
            <option value={NO_PUSH}>No push yet</option>
          </select>
          <select
            value={criteria.warehouseCodes?.[0] || ""}
            onChange={function (e) {
              setCrit({ warehouseCodes: e.target.value ? [e.target.value] : [] });
            }}
            style={selectStyle}
            aria-label="Warehouse"
          >
            <option value="">All warehouses</option>
            {warehouseOptions.map(function (w) {
              return (
                <option key={w} value={w}>
                  {w}
                </option>
              );
            })}
          </select>
          <input
            type="date"
            value={criteria.dateFrom || ""}
            onChange={function (e) {
              setCrit({ dateFrom: e.target.value });
            }}
            style={selectStyle}
            aria-label="Order date from"
          />
          <input
            type="date"
            value={criteria.dateTo || ""}
            onChange={function (e) {
              setCrit({ dateTo: e.target.value });
            }}
            style={selectStyle}
            aria-label="Order date to"
          />
          <label
            style={{
              fontSize: 12,
              color: "#455A64",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <input
              type="checkbox"
              checked={!!criteria.zzzOnly}
              onChange={function (e) {
                setCrit({ zzzOnly: e.target.checked });
              }}
            />
            ZZZ only
          </label>
          <select
            value={criteria.apiExcluded || ""}
            onChange={function (e) {
              setCrit({ apiExcluded: e.target.value || undefined });
            }}
            style={selectStyle}
            aria-label="API excluded"
          >
            <option value="">API: all</option>
            <option value="no">API: not excluded</option>
            <option value="yes">API: excluded</option>
          </select>
          <select
            value={criteria.exported || ""}
            onChange={function (e) {
              setCrit({ exported: e.target.value || undefined });
            }}
            style={selectStyle}
            aria-label="Exported"
          >
            <option value="">Export: all</option>
            <option value="no">Export: not yet</option>
            <option value="yes">Export: exported</option>
          </select>
          <button
            type="button"
            onClick={function () {
              setInspectOpen(true);
            }}
            style={{
              marginLeft: "auto",
              background: "white",
              color: "#00695C",
              border: "1px solid #00897B",
              borderRadius: 6,
              padding: "7px 14px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Inspect Sales Orders on Unleashed
          </button>
          <button
            type="button"
            onClick={function () {
              downloadCsv(toUnleashedCsv(filtered), csvFilename());
            }}
            disabled={filtered.length === 0}
            style={{
              background: filtered.length === 0 ? "#B0BEC5" : "#00897B",
              color: "white",
              border: "none",
              borderRadius: 6,
              padding: "7px 14px",
              fontSize: 13,
              fontWeight: 600,
              cursor: filtered.length === 0 ? "default" : "pointer",
            }}
          >
            Export CSV ({filtered.length})
          </button>
          <span style={{ fontSize: 12, color: "#607D79" }}>
            {filtered.length} order{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        {picked.size > 0 && (
          <div
            style={{
              padding: "10px 16px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              background: "#F1F8F7",
              borderBottom: "1px solid #E0EDEB",
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: "#00695C" }}>
              {picked.size} selected
            </span>
            <button type="button" style={bulkBtn} disabled={busy} onClick={exportPicked}>
              Export selected
            </button>
            <button
              type="button"
              style={bulkBtn}
              disabled={busy}
              onClick={function () {
                runBulk(function (nums) {
                  return setExportedForOrders(nums, new Date().toISOString());
                });
              }}
            >
              Mark exported
            </button>
            <button
              type="button"
              style={bulkBtn}
              disabled={busy}
              onClick={function () {
                runBulk(function (nums) {
                  return setExportedForOrders(nums, null);
                });
              }}
            >
              Allow re-export
            </button>
            <button
              type="button"
              style={bulkBtn}
              disabled={busy}
              onClick={function () {
                runBulk(function (nums) {
                  return setApiExcludedForOrders(nums, true);
                });
              }}
            >
              Exclude from API
            </button>
            <button
              type="button"
              style={bulkBtn}
              disabled={busy}
              onClick={function () {
                runBulk(function (nums) {
                  return setApiExcludedForOrders(nums, false);
                });
              }}
            >
              Release to API
            </button>
            <button
              type="button"
              style={{ ...bulkBtn, color: "#607D79", borderColor: "#CFD8DC" }}
              disabled={busy}
              onClick={function () {
                setPicked(new Set());
              }}
            >
              Clear
            </button>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#607D79" }}>
            Loading sales orders…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#607D79" }}>
            {orders.length === 0
              ? "No sales orders yet — they'll appear here once the Order Builder creates them."
              : "No orders match your search."}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#FAFAFA" }}>
                  <th style={{ ...th, width: 28 }}>
                    <input
                      type="checkbox"
                      checked={allFilteredPicked}
                      onChange={toggleAll}
                      aria-label="Select all"
                    />
                  </th>
                  <th style={th}>Order No.</th>
                  <th style={th}>Type</th>
                  <th style={th}>Customer Code</th>
                  <th style={th}>Reference</th>
                  <th style={th}>Order Date</th>
                  <th style={th}>Warehouse</th>
                  <th style={th}>Push</th>
                  <th style={{ ...th, textAlign: "right" }}>Lines</th>
                </tr>
              </thead>
              <tbody>
                {pagedGroups.map(function (group) {
                  const isOpen = expanded.has(group.key);
                  const groupPicked = group.orders.every(function (o) {
                    return picked.has(o.orderNumber);
                  });
                  const totalLines = group.orders.reduce(function (sum, o) {
                    return sum + o.lineCount;
                  }, 0);
                  const roll = rollupStatus(
                    group.orders.map(function (o) {
                      return pushByOrder[o.orderNumber]?.status;
                    })
                  );
                  const first = group.orders[0].header;
                  return (
                    <React.Fragment key={group.key}>
                      <tr
                        onClick={function () {
                          toggleExpand(group.key);
                        }}
                        style={{ cursor: "pointer", background: "#FAFCFC" }}
                      >
                        <td style={td}>
                          <input
                            type="checkbox"
                            checked={groupPicked}
                            onClick={function (e) {
                              e.stopPropagation();
                            }}
                            onChange={function () {
                              toggleGroup(group);
                            }}
                            aria-label={`Select all orders for ${group.ref}`}
                          />
                        </td>
                        <td style={{ ...td, fontWeight: 700, color: "#00695C" }}>
                          <span style={{ display: "inline-block", width: 16, color: "#607D79" }}>
                            {isOpen ? "▾" : "▸"}
                          </span>
                          {group.ref}
                        </td>
                        <td style={td}>—</td>
                        <td style={td}>{dash(group.customerCode)}</td>
                        <td style={{ ...td, color: "#607D79" }}>
                          {group.orders.length} order{group.orders.length !== 1 ? "s" : ""}
                        </td>
                        <td style={td}>{dash(first.order_date)}</td>
                        <td style={td}>{dash(first.warehouse_code)}</td>
                        <td style={td}>
                          <StatusBadge status={roll || undefined} />
                        </td>
                        <td style={{ ...td, textAlign: "right" }}>{totalLines}</td>
                      </tr>
                      {isOpen &&
                        group.orders.map(function (o) {
                          const h = o.header;
                          const p = pushByOrder[o.orderNumber];
                          return (
                            <tr
                              key={o.orderNumber}
                              onClick={function () {
                                setSelected(o);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <td style={td}>
                                <input
                                  type="checkbox"
                                  checked={picked.has(o.orderNumber)}
                                  onClick={function (e) {
                                    e.stopPropagation();
                                  }}
                                  onChange={function () {
                                    toggleOne(o.orderNumber);
                                  }}
                                  aria-label={`Select ${o.orderNumber}`}
                                />
                              </td>
                              <td
                                style={{
                                  ...td,
                                  fontWeight: 700,
                                  color: "#00897B",
                                  paddingLeft: 32,
                                }}
                              >
                                {dash(o.orderNumber)}
                              </td>
                              <td style={{ ...td, fontWeight: 600, color: "#00695C" }}>
                                {dash(p?.suffix)}
                              </td>
                              <td style={td}>{dash(h.customer_code)}</td>
                              <td style={td}>{dash(h.customer_reference)}</td>
                              <td style={td}>{dash(h.order_date)}</td>
                              <td style={td}>{dash(h.warehouse_code)}</td>
                              <td style={td}>
                                <StatusBadge status={p?.status} />
                                {p?.api_excluded && (
                                  <span
                                    style={{ ...chip, background: "#ECEFF1", color: "#455A64" }}
                                  >
                                    Excluded
                                  </span>
                                )}
                                {p?.exported_at && (
                                  <span
                                    style={{ ...chip, background: "#E8F5E9", color: "#2E7D32" }}
                                  >
                                    Exported
                                  </span>
                                )}
                              </td>
                              <td style={{ ...td, textAlign: "right" }}>{o.lineCount}</td>
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {soTotalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: 16,
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  onClick={function () {
                    setSoPage(Math.max(0, soSafePage - 1));
                  }}
                  disabled={soSafePage === 0}
                  style={{
                    border: "1px solid #CFD8DC",
                    background: "white",
                    color: "#37474F",
                    borderRadius: 6,
                    padding: "4px 10px",
                    fontFamily: "inherit",
                    cursor: soSafePage === 0 ? "default" : "pointer",
                    opacity: soSafePage === 0 ? 0.4 : 1,
                  }}
                >
                  Prev
                </button>
                {Array.from({ length: soTotalPages }, function (_, i) {
                  return i;
                }).map(function (i) {
                  const active = i === soSafePage;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={function () {
                        setSoPage(i);
                      }}
                      style={{
                        border: active ? "1px solid #00695C" : "1px solid #CFD8DC",
                        background: active ? "#00695C" : "white",
                        color: active ? "white" : "#37474F",
                        fontWeight: active ? 700 : 400,
                        borderRadius: 6,
                        padding: "4px 11px",
                        fontSize: 13,
                        fontFamily: "inherit",
                        cursor: "pointer",
                      }}
                    >
                      {i + 1}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={function () {
                    setSoPage(Math.min(soTotalPages - 1, soSafePage + 1));
                  }}
                  disabled={soSafePage >= soTotalPages - 1}
                  style={{
                    border: "1px solid #CFD8DC",
                    background: "white",
                    color: "#37474F",
                    borderRadius: 6,
                    padding: "4px 10px",
                    fontFamily: "inherit",
                    cursor: soSafePage >= soTotalPages - 1 ? "default" : "pointer",
                    opacity: soSafePage >= soTotalPages - 1 ? 0.4 : 1,
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {selected && (
        <ModalBackdrop
          draggable
          dragTitle={`Sales Order ${selected.orderNumber}`}
          onClose={function () {
            setSelected(null);
          }}
          contentStyle={{
            background: "#F1F5F4",
            borderRadius: 12,
            padding: 0,
            width: "min(1150px, calc(100vw - 40px))",
          }}
        >
          <div style={{ padding: "18px 22px" }}>
            <SalesOrderDetail
              order={selected}
              push={pushByOrder[selected.orderNumber]}
              onBack={function () {
                setSelected(null);
              }}
              onRequeue={handleRequeue}
            />
          </div>
        </ModalBackdrop>
      )}

      {inspectOpen && (
        <SalesOrderInspectorModal
          onClose={function () {
            setInspectOpen(false);
          }}
        />
      )}
    </div>
  );
}
